// src/components/patient/overview/SOCChecklistWidget.tsx
import { CheckCircle, Circle, FileUp } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { SOCChecklist } from '../../../dtos/patientmr/certificationPeriod.dto';
import type { ConsentDocumentContentType, DmeEquipmentCode } from '../../../dtos/patientmr/demographics.dto';
import type { Discipline } from './types';
import { DISCIPLINE_COLORS } from './types';
import { SocItemsService } from '../../../services/patientmr/socItems.service';
import { DME_EQUIPMENT_OPTIONS } from '../../../constants/patientLifecycle';
import { notifyPatientChartUpdated } from '../../../constants/navigationEvents';
import { toIsoDate } from '../../../services/patientmr/patientLifecycle.service';
import { PatientDocumentsService } from '../../../services/patientmr/patientDocuments.service';
import { useHasAnyPermission } from '../../../hooks/usePermission';
import type { PermissionString } from '../../../types/permissions';
import { getDisciplineEntry } from '../../../constants/disciplines';

// ─── Item definitions ─────────────────────────────────────────────────────────

interface SOCChecklistItem {
  key:      keyof SOCChecklist;
  label:    string;
  route:    (patientId: string, discipline?: string) => string;
}

/** Items completed in place (manual confirmation saved to demographics) */
type ManualKey = 'consentObtained' | 'dmeConfirmed' | 'emergencyPlanOnFile' | 'hhaxSetupComplete';
const MANUAL_KEYS: ManualKey[] = ['consentObtained', 'dmeConfirmed', 'emergencyPlanOnFile', 'hhaxSetupComplete'];
const isManualKey = (key: keyof SOCChecklist): key is ManualKey => (MANUAL_KEYS as string[]).includes(key as string);

/** Shared (patient-level) — identical across all discipline tracks */
const SHARED_ITEMS: SOCChecklistItem[] = [
  { key: 'demographicsComplete',   label: 'Demographics Complete',  route: (id) => `/patients/${id}/demographics`       },
  { key: 'insuranceVerified',      label: 'Insurance Verified',     route: (id) => `/patients/${id}/insurance`          },
  { key: 'physicianOrderComplete', label: 'Physician Order Signed', route: (id) => `/patients/${id}/poc-certifications` },
  { key: 'consentObtained',         label: 'Consent Obtained',         route: (id) => `/patients/${id}/demographics`       },
  { key: 'dmeConfirmed',            label: 'DME Confirmed',             route: (id) => `/patients/${id}/demographics`       },
  { key: 'emergencyPlanOnFile',     label: 'Emergency Plan on File',    route: (id) => `/patients/${id}/demographics`       },
  // PRD item 12 (Phase 7): only when EVV is required; the server omits the key otherwise
  { key: 'hhaxSetupComplete',       label: 'HHAX Setup (EVV)',          route: (id) => `/patients/${id}/demographics`       },
];

/**
 * Items that apply: conditional items the server omitted are hidden (HHAX setup), and Phase 20A
 * a discipline without an assessment or care-plan form (MSW, HHA) is not shown those steps.
 */
const applicable = (items: SOCChecklistItem[], checklist: SOCChecklist, discipline?: string | null): SOCChecklistItem[] => {
  const forms = discipline ? getDisciplineEntry(discipline)?.forms : undefined;
  return items.filter(item => {
    if (item.key === 'hhaxSetupComplete') return item.key in checklist;
    if (item.key === 'socAssessmentComplete' && forms && !forms.assessment) return false;
    if (item.key === 'carePlansComplete' && forms && !forms.carePlan) return false;
    return true;
  });
};

/** Per-discipline — each item is specific to the selected discipline */
const DISCIPLINE_ITEMS: SOCChecklistItem[] = [
  { key: 'referralReceived',      label: '{DISC} Referral',          route: (id, d) => `/patients/${id}/referrals?discipline=${d}`      },
  { key: 'careTeamAssigned',      label: '{DISC} Provider Assigned', route: (id, d) => `/patients/${id}/team?discipline=${d}`           },
  { key: 'socAssessmentComplete', label: '{DISC} SOC Assessment',    route: (id, d) => `/patients/${id}/assessments?discipline=${d}`    },
  { key: 'carePlansComplete',     label: '{DISC} Care Plan',         route: (id, d) => `/patients/${id}/careplans?discipline=${d}`      },
  { key: 'priorAuthComplete',     label: '{DISC} PA Approved',       route: (id, d) => `/patients/${id}/authorizations?discipline=${d}` },
];

/** Consent document limits (the server enforces the same on the pre-signed upload, Phase 8) */
const CONSENT_TYPES: ConsentDocumentContentType[] = ['application/pdf', 'image/jpeg', 'image/png'];
const CONSENT_MAX_BYTES = 10 * 1024 * 1024;
/** PAT-09: attaching the consent form is a documents-library upload */
const CONSENT_UPLOAD_PERMS = ['patientmr:phi:documents:create', 'patientmr:phi:documents:createall'] as PermissionString[];
/** Marking the manual items uses the narrow SOC checklist route, not demographics:update */
const SOC_ITEM_UPDATE_PERMS = ['patientmr:phi:soc-checklist:update', 'patientmr:phi:soc-checklist:updateall'] as PermissionString[];

/** Client-side check for the consent file. Returns an error message or null. */
export function validateConsentFile(file: Pick<File, 'type' | 'size'>): string | null {
  if (!(CONSENT_TYPES as string[]).includes(file.type)) return 'The consent form must be a PDF, JPEG or PNG file.';
  if (file.size > CONSENT_MAX_BYTES) return 'The consent form must be 10 MB or smaller.';
  return null;
}

// ─── Section renderer ─────────────────────────────────────────────────────────

interface SectionProps {
  title:          string;
  items:          SOCChecklistItem[];
  checklist:      SOCChecklist;
  patientId:      string;
  discipline?:    string;
  sectionBg:      string;  // Tailwind bg class
  sectionBorder:  string;  // Tailwind border class
  onNavigate?:    (path: string) => void;
  onChecklistChange?: () => void;
}

function ChecklistSection({ title, items, checklist, patientId, discipline, sectionBg, sectionBorder, onNavigate, onChecklistChange }: SectionProps) {
  const resolvedLabel = (label: string) => label.replace('{DISC}', discipline ?? '');
  const [editingKey, setEditingKey] = useState<ManualKey | null>(null);
  const [notes, setNotes] = useState('');
  const [date, setDate] = useState(toIsoDate(new Date()));
  const [consentFile, setConsentFile] = useState<File | null>(null);
  const canUploadConsent = useHasAnyPermission(CONSENT_UPLOAD_PERMS);
  const canMarkItems = useHasAnyPermission(SOC_ITEM_UPDATE_PERMS);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [equipment, setEquipment] = useState<DmeEquipmentCode[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Saved locally and waiting for the refreshed dashboard to confirm completion */
  const [awaitingServer, setAwaitingServer] = useState<Partial<Record<ManualKey, boolean>>>({});

  // Once the refreshed checklist confirms an item, stop showing "updating"
  useEffect(() => {
    setAwaitingServer((prev) => {
      const next = { ...prev };
      MANUAL_KEYS.forEach((key) => {
        if (checklist[key]) delete next[key];
      });
      return next;
    });
  }, [checklist]);

  const openEditor = (key: ManualKey) => {
    setEditingKey(key);
    setNotes('');
    setDate(toIsoDate(new Date()));
    setConsentFile(null);
    setEquipment([]);
    setError(null);
  };

  const handleFile = (file: File | undefined) => {
    setConsentFile(null);
    if (!file) return;
    const validation = validateConsentFile(file);
    if (validation) {
      setError(validation);
      return;
    }
    setError(null);
    setConsentFile(file);
  };

  const completeManualItem = async (key: ManualKey) => {
    if (!date) {
      setError('A date is required.');
      return;
    }
    if (key === 'dmeConfirmed' && equipment.length === 0 && !notes.trim()) {
      setError('Select the equipment in the home, or add a note (for example "No DME needed").');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Each item has its own route that writes only that item's fields (soc-checklist:update)
      const note = notes.trim() || null;
      if (key === 'consentObtained') {
        let consentDocumentId: string | undefined;
        if (consentFile && canUploadConsent) {
          // Phase 8: the form goes to the documents library (type CONSENT); demographics keep its id
          setUploadPct(0);
          const doc = await PatientDocumentsService.upload(
            patientId,
            consentFile,
            { type: 'CONSENT', documentDate: date, title: 'Consent form' },
            (pct) => setUploadPct(pct)
          );
          consentDocumentId = doc.documentId;
        }
        await SocItemsService.update(patientId, 'consent', {
          consentObtained: true,
          consentDate: date,
          consentNote: note,
          ...(consentDocumentId ? { consentDocumentId } : {}),
        });
      } else if (key === 'dmeConfirmed') {
        await SocItemsService.update(patientId, 'dme', {
          dmeConfirmed: true, dmeConfirmedDate: date, dmeEquipment: equipment, dmeNote: note,
        });
      } else if (key === 'hhaxSetupComplete') {
        await SocItemsService.update(patientId, 'hhax-setup', {
          hhaxSetupComplete: true, hhaxSetupDate: date, hhaxSetupNote: note,
        });
      } else {
        await SocItemsService.update(patientId, 'emergency-plan', {
          emergencyPlanOnFile: true, emergencyPlanDate: date, emergencyPlanNote: note,
        });
      }
      setAwaitingServer(prev => ({ ...prev, [key]: true }));
      setEditingKey(null);
      // Completion comes from the refreshed dashboard, not local state
      onChecklistChange?.();
      notifyPatientChartUpdated(patientId);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'The item was not saved. Try again.');
    } finally {
      setSaving(false);
      setUploadPct(null);
    }
  };

  return (
    <div className={`rounded-lg border overflow-hidden ${sectionBorder}`}>
      <div className={`px-3 py-1.5 ${sectionBg}`}>
        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{title}</p>
      </div>
      <div className="divide-y divide-slate-100">
        {applicable(items, checklist).map((item) => {
          const done = !!checklist[item.key];
          const pending = isManualKey(item.key) && !done && !!awaitingServer[item.key];
          const route = item.route(patientId, discipline);
          const readOnlyManual = isManualKey(item.key) && !canMarkItems;

          return (
            <div key={item.key}>
              <button
              type="button"
              disabled={readOnlyManual}
              title={readOnlyManual && !done ? 'You do not have permission to mark this item' : undefined}
              onClick={() => {
                if (isManualKey(item.key)) {
                  if (!done && canMarkItems) openEditor(item.key);
                } else {
                  onNavigate?.(route);
                }
              }}
              className={`w-full flex items-center px-3 py-2.5 transition-colors group text-left ${
                done ? 'hover:bg-slate-50' : 'bg-slate-50/50 hover:bg-slate-100/50'
              }`}
            >
              <div className="flex-shrink-0 mr-3">
                {done
                  ? <CheckCircle className="w-4 h-4 text-emerald-600" />
                  : <Circle className="w-4 h-4 text-slate-400 group-hover:text-slate-500" />}
              </div>
              <span className={`text-sm flex-1 ${
                done ? 'text-slate-400' : 'text-slate-800 font-medium'
              }`}>
                {resolvedLabel(item.label)}
              </span>
              {pending && <span className="text-xs text-slate-500">Saved — updating…</span>}
              </button>
              {editingKey === item.key && !done && canMarkItems && (
              <div className="px-10 pb-3 space-y-2 bg-slate-50">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label className="text-xs text-slate-600">
                    Date <span className="text-red-600" aria-hidden="true">*</span>
                    <input type="date" required value={date} onChange={event => setDate(event.target.value)} className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm" />
                  </label>
                  <label className="text-xs text-slate-600">
                    Notes
                    <input value={notes} onChange={event => setNotes(event.target.value)} className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm" placeholder={item.key === 'emergencyPlanOnFile' ? 'Where the plan is kept' : item.key === 'hhaxSetupComplete' ? 'e.g. Member added in HHAX with Medicaid ID' : 'Optional notes'} />
                  </label>
                </div>
                {item.key === 'dmeConfirmed' && (
                  <fieldset className="text-xs text-slate-600">
                    <legend className="mb-1">Equipment in the home</legend>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {DME_EQUIPMENT_OPTIONS.map((option) => (
                        <label key={option.value} className="flex items-center gap-1.5 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={equipment.includes(option.value)}
                            onChange={(event) =>
                              setEquipment((prev) =>
                                event.target.checked ? [...prev, option.value] : prev.filter((v) => v !== option.value)
                              )
                            }
                          />
                          {option.label}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                )}
                {item.key === 'consentObtained' && canUploadConsent && (
                  <label className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                    <FileUp className="w-4 h-4" />
                    Attach consent form (PDF, JPEG or PNG, up to 10 MB)
                    <input type="file" aria-label="Consent form file" accept="application/pdf,image/jpeg,image/png" onChange={event => handleFile(event.target.files?.[0])} className="text-xs" />
                  </label>
                )}
                {item.key === 'consentObtained' && !canUploadConsent && (
                  <p className="text-xs text-slate-500">You can mark consent, but attaching the form needs permission to upload patient documents.</p>
                )}
                {item.key === 'consentObtained' && uploadPct !== null && (
                  <div className="h-1.5 w-full overflow-hidden rounded bg-slate-200" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={uploadPct}>
                    <div className="h-1.5 bg-slate-700" style={{ width: `${uploadPct}%` }} />
                  </div>
                )}
                {error && <p role="alert" className="text-xs text-red-700">{error}</p>}
                <div className="flex gap-2">
                  <button type="button" disabled={saving || !date} onClick={() => completeManualItem(item.key as ManualKey)} className="rounded bg-slate-800 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">
                    {saving ? 'Saving...' : 'Mark Complete'}
                  </button>
                  <button type="button" onClick={() => setEditingKey(null)} className="rounded border border-slate-300 px-3 py-1.5 text-xs text-slate-700">Cancel</button>
                </div>
              </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface SOCChecklistWidgetProps {
  checklist:        SOCChecklist | undefined | null;
  patientId:        string;
  /** When provided, renders split shared + per-discipline view */
  discipline?:      Discipline;
  /** Dashboard-computed completion for the track (Phase 6); preferred over counting flags */
  isComplete?:      boolean;
  onNavigate?:      (path: string) => void;
  onChecklistChange?: () => void;
}

export default function SOCChecklistWidget({
  checklist,
  patientId,
  discipline,
  isComplete,
  onNavigate,
  onChecklistChange,
}: SOCChecklistWidgetProps) {
  if (!checklist) return null;

  const allItems       = applicable([...SHARED_ITEMS, ...DISCIPLINE_ITEMS], checklist, discipline);
  const totalCount     = allItems.length;
  const completedCount = allItems.filter(item => checklist[item.key]).length;
  const progressPct    = isComplete ? 100 : Math.round((completedCount / totalCount) * 100);

  const colors = discipline ? DISCIPLINE_COLORS[discipline] : null;

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className={`px-4 py-3 border-b flex items-center justify-between ${colors ? `${colors.light} border-${colors.border}` : 'bg-slate-50 border-slate-200'}`}>
        <div>
          <h3 className={`text-sm font-semibold ${colors ? colors.label : 'text-slate-800'}`}>
            {discipline ? `${discipline} Onboarding Checklist` : 'SOC Onboarding Checklist'}
          </h3>
          <p className={`text-xs mt-0.5 ${colors ? colors.label : 'text-slate-600'}`}>
            {isComplete ? 'Complete' : `${completedCount} of ${totalCount} complete`}
          </p>
        </div>
        <span className={`text-lg font-bold ${colors ? colors.label : 'text-slate-700'}`}>{progressPct}%</span>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-slate-100 h-1.5">
        <div
          className={`h-1.5 transition-all duration-500 ${colors ? colors.bar : 'bg-slate-500'}`}
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {/* Checklist — split view when discipline is provided, flat view otherwise */}
      <div className="p-3 space-y-2">
        {discipline ? (
          <>
            <ChecklistSection
              title="Patient Setup"
              items={SHARED_ITEMS}
              checklist={checklist}
              patientId={patientId}
              sectionBg="bg-slate-50"
              sectionBorder="border-slate-200"
              onNavigate={onNavigate}
              onChecklistChange={onChecklistChange}
            />
            <ChecklistSection
              title={`${discipline} Onboarding`}
              items={applicable(DISCIPLINE_ITEMS, checklist, discipline)}
              checklist={checklist}
              patientId={patientId}
              discipline={discipline}
              sectionBg={colors!.light}
              sectionBorder={colors!.border}
              onNavigate={onNavigate}
              onChecklistChange={onChecklistChange}
            />
          </>
        ) : (
          <ChecklistSection
            title="Onboarding Steps"
            items={allItems}
            checklist={checklist}
            patientId={patientId}
            sectionBg="bg-slate-50"
            sectionBorder="border-slate-200"
            onNavigate={onNavigate}
            onChecklistChange={onChecklistChange}
          />
        )}
      </div>

    </div>
  );
}
